import Footer from "@src/components/footer/new/Footer";
import { ProgressBar } from "@src/components/progressBar/ProgressBar";
import { SidePane } from "@src/components/sidePane/SidePane";
import { TopBar } from "@src/components/topbar/Topbar";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import "./stepper.scss";
import {
  ProjetPreSouscription,
  ProjetSignature,
  ProjetSouscription,
} from "@src/App";
import { StepName, StepProps, StepsData, STEPS } from "@src/Steps/step/Steps";
import { StepLoader } from "@src/StepLoader";

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer: Record<string, any>[];
  }
}
type StepperProps = {
  projetSouscription?: ProjetSouscription;
  projetSignature?: ProjetSignature;
  projetPreSouscription?: ProjetPreSouscription;
  setProjetPreSouscription: Dispatch<
    SetStateAction<ProjetPreSouscription | undefined>
  >;
  setProjetSignature: Dispatch<SetStateAction<ProjetSignature | undefined>>;
  hasFinished: boolean;
  projectId?: string;
};

type Transition = {
  direction: "forward" | "backward";
  nextStep: StepName;
};

type StepperState = {
  activeStep: StepName;
  transition?: Transition;
};

function getInitialStep(
  hasUrlSignature: boolean,
  hasProjetPreSouscription: boolean,
  hasProjetSouscription: boolean,
  hasProjetSignature: boolean,
  hasFinished: boolean
): StepName {
  void hasUrlSignature; // eslint-disable-line no-void
  void hasProjetSouscription; // eslint-disable-line no-void
  void hasProjetSignature; // eslint-disable-line no-void
  void hasFinished; // eslint-disable-line no-void

  if (hasFinished) return "SUCCESS";
  if (hasUrlSignature) return "RECAP_SIGNATURE";
  if (hasProjetSignature) return "PIECES_JUSTIFICATIVES";
  if (hasProjetSouscription) return "RECAP_SOUSCRIPTION";
  if (hasProjetPreSouscription) return "SUBSCRIPTION_INTRODUCTION";

  return "LANDING";
}

function getInitialData(
  projetPreSouscription?: ProjetPreSouscription,
  projetSouscription?: ProjetSouscription
): StepsData {
  const tempData: StepsData = {
    LANDING: undefined,
    BIRTHDATE: {},
    SITUATION: undefined,
    ACTIVITIES: {
      sports: false,
      cycling: false,
      hiking: false,
      cooking: false,
      gardening: false,
      handiwork: false,
      outings: false,
    },
    TARIF: {
      name: "",
      simulationCotisation: 0,
      renteMensuelle: "",
      capitalForfaitaire: "",
    },
    CONTACT_INFOS: {},
    ADDRESS_INFOS: {
      address: "",
      complement: "",
      zipcode: "",
      city: "",
      mobilePhone: "",
      fixedPhone: "",
      email: "",
      consent: false,
      accept: false,
    },
    RECAP: undefined,
    CONFIRMATION_SIMULATION: undefined,
    SUBSCRIPTION_INTRODUCTION: undefined,
    VALIDATE_POINTS: undefined,
    CHECK_YOUR_MAIL: undefined,
    RECAP_SOUSCRIPTION: undefined,
    AVANT_DE_POURSUIVRE: undefined,
    NUM_SECU: undefined,
    POLITIQUEMENT_EXPOSEE: {
      assurePPE: undefined,
      assurePPEFonction: undefined,
      assurePPECessation: undefined,
      prochePPE: undefined,
      prochePPEFonction: undefined,
      prochePPECessation: undefined,
    },
    BENEFICIAIRES: {
      hasBeneficiaire: undefined,
      isPersonnePhysique: undefined,
      hasEqualShares: undefined,
      beneficiaries: undefined,
      noPersonnePhysique: undefined,
    },
    CONSENT: {
      consentSincere: false,
      consentRisque: false,
      consentModify: false,
      consentNoComplete: false,
      consentLaw: false,
    },
    CONSENT_SOUSCRIPTION: undefined,
    MANDAT_PRELEVEMENT: undefined,
    PIECES_JUSTIFICATIVES: {},
    RECAP_SIGNATURE: undefined,
    SUCCESS: undefined,
  };

  if (projetPreSouscription) {
    tempData.SITUATION = {
      ...tempData.SITUATION,
      isMemberTutelaire: projetPreSouscription?.questions.isMemberTutelaire,
    };
    tempData.BIRTHDATE = {
      ...tempData.BIRTHDATE,
      birthDate: new Date(projetPreSouscription.contact.birthDate),
    };

    tempData.TARIF = {
      ...tempData.TARIF,
      name: projetPreSouscription.tarif.name,
      simulationCotisation: projetPreSouscription.tarif.simulationCotisation,
    };

    tempData.CONTACT_INFOS = {
      ...tempData.CONTACT_INFOS,
      civility: projetPreSouscription.contact.civility,
      name: projetPreSouscription.contact.name,
      surname: projetPreSouscription.contact.surname,
    };

    tempData.ADDRESS_INFOS = {
      ...tempData.ADDRESS_INFOS,
      address: projetPreSouscription.contact.address,
      zipcode: projetPreSouscription.contact.zipcode,
      city: projetPreSouscription.contact.city,
      mobilePhone: projetPreSouscription.contact.mobile,
      email: projetPreSouscription.contact.mail,
    };
  }

  if (projetSouscription) {
    tempData.BIRTHDATE = {
      ...tempData.BIRTHDATE,
      birthDate: new Date(projetSouscription.assure.birthDate),
    };

    tempData.CONTACT_INFOS = {
      ...tempData.CONTACT_INFOS,
      civility: projetSouscription.assure.civility,
      name: projetSouscription.assure.name,
      surname: projetSouscription.assure.surname,
    };

    tempData.ADDRESS_INFOS = {
      ...tempData.ADDRESS_INFOS,
      address: projetSouscription.assure.address,
      zipcode: projetSouscription.assure.zipcode,
      city: projetSouscription.assure.city,
      mobilePhone: projetSouscription.assure.mobile,
      email: projetSouscription.assure.mail,
    };
  }
  return tempData;
}

const TRANSITION_DURATION = 200;
function goToStep(
  state: StepperState,
  setState: Dispatch<SetStateAction<StepperState>>,
  nextStep: StepName,
  direction: "forward" | "backward"
) {
  if (state.transition) return;
  setState({
    ...state,
    transition: {
      direction,
      nextStep,
    },
  });
  setTimeout(() => {
    setState({
      activeStep: nextStep,
    });
  }, TRANSITION_DURATION);
}

export function Stepper({
  projetPreSouscription,
  projetSouscription,
  projetSignature,
  setProjetSignature,
  setProjetPreSouscription,
  hasFinished,
  projectId,
}: StepperProps) {
  const [state, setState] = useState<StepperState>({
    activeStep: getInitialStep(
      !!projetSignature?.urlSignature,
      !!projetPreSouscription,
      !!projetSouscription,
      !!projetSignature,
      hasFinished
    ),
  });
  const [stepsData, setStepData] = useState<StepsData>(
    getInitialData(projetPreSouscription, projetSouscription)
  );

  useEffect(() => {
    window.dataLayer?.push({
      activeStep: state.activeStep,
      event: "TPV",
    });
  }, [state.activeStep]);

  const [isSidePaneOpen, setIsSidePaneOpen] = useState(false);

  const LoadedComp = STEPS[state.activeStep].component as (
    props: StepProps<unknown>
  ) => JSX.Element;

  return (
    <>
      <TopBar activeMacroStep={STEPS[state.activeStep].macroStep} />
      <div className="stepper">
        <div className="stepper__progress-bar-container stepper__centered-container">
          <div className="stepper__centered-content">
            <ProgressBar
              hidden={false}
              progress={
                STEPS[state.transition?.nextStep || state.activeStep].progress
              }
            />
          </div>
        </div>
        <div className="stepper__step-container">
          <fieldset
            className="stepper__step stepper__fieldset"
            data-transition={state.transition?.direction}
            data-hidden={false}
            disabled={isSidePaneOpen}
          >
            <StepLoader key={state.activeStep}>
              <LoadedComp
                projectId={projectId}
                projetPreSouscription={projetPreSouscription}
                projetSouscription={projetSouscription}
                projetSignature={projetSignature}
                setProjetPreSouscription={setProjetPreSouscription}
                setProjetSignature={setProjetSignature}
                data={stepsData[state.activeStep as keyof typeof stepsData]}
                stepsData={stepsData}
                setData={(data: unknown) => {
                  setStepData(
                    (prevState) =>
                      ({
                        ...prevState,
                        [state.activeStep]: data,
                      } as unknown as StepsData)
                  );
                }}
                changeStep={(
                  stepName: StepName,
                  transitionDirection: "forward" | "backward"
                ) => {
                  setTimeout(() => {
                    window.scrollTo({ top: 0 });
                  }, 200);
                  goToStep(state, setState, stepName, transitionDirection);
                }}
              />

              <Footer stepName={state.activeStep} />
            </StepLoader>
          </fieldset>
        </div>
      </div>
      <SidePane
        isOpen={isSidePaneOpen}
        setIsOpen={setIsSidePaneOpen}
        activeStep={state.transition?.nextStep || state.activeStep}
        hidden={false}
      />
    </>
  );
}
